import { APIPaths } from '../common/constants';
import interceptor, { OperationType } from '../common/interceptor/interceptor';
import { ApiResponse, MenuItemData } from '@/apis/types';
import type { OktaBootstrapConfig } from '@/config/okta-config';
import {
  getActiveRefreshToken,
  getActiveToken,
} from '@/common/helper/tokenStorage';

// WebSocket short-lived ticket
export interface WsTicketResponse {
  data: {
    ticket: string;
    expires_in: number;
  };
}

/**
 * Requests a short-lived single-use WebSocket ticket from the backend.
 * The ticket is valid for 60 seconds and must be passed as ?ticket=<uuid>
 * when opening a WebSocket connection instead of the raw JWT token.
 *
 * Retries up to 2 times on 503 (Gunicorn worker cold-start) before throwing.
 */
export const GetWsTicket = async (): Promise<WsTicketResponse> => {
  const MAX_ATTEMPTS = 3;
  let lastError: unknown;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      return await interceptor.axiosPost(
        'Post',
        APIPaths.wsTicket,
        {},
        null,
        null,
        OperationType.NonBlockedWrite
      );
    } catch (err: unknown) {
      lastError = err;
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      // Only retry on 503 (server not yet ready); any other error falls through immediately
      if (status !== 503) throw err;
      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise((resolve) =>
          setTimeout(resolve, 300 * (attempt + 1))
        );
      }
    }
  }
  throw lastError;
};

// Sign Up form
export interface SignUpUserValues {
  name: string;
  email: string;
  company_name: string;
}

interface RefreshResponse {
  data: {
    access: string;
    refresh: string;
    [key: string]: unknown; // in case backend returns other fields
  };
}

interface LogoutResponse {
  data: {
    data: {
      access: string;
      refresh: string;
      [key: string]: unknown;
    };
  };
}

export const SignUpUser = (model: SignUpUserValues): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosLogin('Post', `/auth/register/?is_signup=true`, model)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

//Email Verify
export interface GetEmailVerifiedResponse {
  data: {
    detail: string;
    data?: {
      is_success: boolean;
    };
  };
}

export const GetEmailVerified = (
  id: string
): Promise<GetEmailVerifiedResponse> => {
  return new Promise((resolve, reject) => {
    const path = `/auth/verify-email/${id}`;
    interceptor
      .axiosLogin('Get', path, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const CreatePasswordApi = (model: unknown): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosLogin('Post', '/auth/set-password/', model)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

// Login form
interface LoginResponse {
  data?: {
    is_success: boolean;
    access?: string;
    refresh?: string;
    data?: {
      message?: string;
    };
  };
}

export interface LoginModel {
  email: string;
  password: string;
}

export const Login = (model: LoginModel): Promise<LoginResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosLogin('Post', '/auth/login/', model)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface LoginWithOktaPayload {
  code: string;
  state: string;
  code_verifier: string;
  redirect_uri: string;
}

export interface OktaSSOConfigResponse {
  data: OktaBootstrapConfig & {
    provider: string;
    source: string;
    company_id?: string | null;
    company_name?: string | null;
  };
}

export const LoginWithOkta = (
  payload: LoginWithOktaPayload
): Promise<LoginResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosLogin('Post', '/auth/login-with-okta/', payload)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const GetOktaSSOConfig = (): Promise<OktaSSOConfigResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosLogin('Get', '/auth/okta/config/', null)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const Refresh = (): Promise<RefreshResponse> => {
  return new Promise((resolve, reject) => {
    const model = {
      // Uses whichever refresh token is active for this tab (preview vs
      // the admin's real session) so an in-progress preview session isn't
      // silently refreshed against -- or overwritten by -- the real one.
      refresh: getActiveRefreshToken(),
    };
    interceptor
      .axiosPost(
        'Post',
        '/auth/login/refresh/',
        model,
        null,
        null,
        OperationType.NonBlockedWrite
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const Logout = (): Promise<LogoutResponse> => {
  return new Promise((resolve, reject) => {
    // Logout terminates the real authenticated account in localStorage.
    const model = {
      refresh: localStorage.getItem('@refreshToken'),
    };
    const token = localStorage.getItem('@token');
    interceptor
      .axiosPost(
        'Post',
        '/auth/logout/',
        model,
        token || undefined,
        null,
        OperationType.NonBlockedWrite
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export const ForgotPasswordApi = (email: string): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosLogin('Post', '/auth/forgot-password/', { email })
      .then((res) => resolve(res))
      .catch((error) => {
        console.error('API Error:', error?.response);
        reject(error?.response);
      });
  });
};

// API call for Get permissions menu

interface GetMenuResponse {
  data?: {
    menus?: MenuItemData[];
  };
  status?: number;
}

export interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

export const GetMenu = (token: string): Promise<GetMenuResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.get_menu, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

// Support Video API
export interface SupportVideoResponse {
  data?: {
    data?: {
      data?: {
        video_url?: string;
        title?: string;
      };
      is_success?: boolean;
    };
  };
}

export const GetSupportVideo = (): Promise<SupportVideoResponse> => {
  return new Promise((resolve, reject) => {
    const token = getActiveToken();
    interceptor
      .axiosGet('Get', '/auth/support/video/', token || undefined, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

// Product Manual API
export interface ProductManualResponse {
  data?: {
    data?: {
      data?: {
        manual_url?: string;
      };
      is_success?: boolean;
    };
  };
}

export const GetProductManual = (): Promise<ProductManualResponse> => {
  return new Promise((resolve, reject) => {
    const token = getActiveToken();
    interceptor
      .axiosGet('Get', '/auth/support/manual/', token || undefined, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface ViewAsUserResponse {
  data?: {
    is_success?: boolean;
    access?: string;
    refresh?: string;
    message?: string;
    data?: {
      data?: { access?: string; refresh?: string };
      message?: string;
      is_success?: boolean;
    };
  };
  access?: string;
  refresh?: string;
}

export const ViewAsUserApi = (
  userId: string | number
): Promise<ViewAsUserResponse> => {
  return new Promise((resolve, reject) => {
    // Intentionally reads the admin's real localStorage token — not
    // getActiveToken() — because at the moment this is called the admin
    // hasn't entered preview yet, so sessionStorage is empty and the
    // admin's real token is the one the backend must authorise against.
    const token = localStorage.getItem('@token');
    interceptor
      .axiosPost(
        'Post',
        '/auth/users/preview/',
        { user_id: String(userId) },
        token || undefined,
        null,
        OperationType.NonBlockedWrite
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response);
      });
  });
};

export interface ExitPreviewResponse {
  data?: {
    data?: {
      data?: {
        access?: string;
        refresh?: string;
      };
      message?: string;
      is_success?: boolean;
    };
  };
}

export const ExitPreviewApi = (): Promise<ExitPreviewResponse> => {
  return new Promise((resolve, reject) => {
    // Use the active (preview) token so the backend can identify which
    // preview session to close via its JWT claims (preview_audit_id, etc.).
    const token = getActiveToken();
    interceptor
      .axiosPost(
        'Post',
        APIPaths.previewExit,
        {},
        token || undefined,
        null,
        OperationType.NonBlockedWrite
      )
      .then((res) => {
        resolve(res as ExitPreviewResponse);
      })
      .catch((error) => {
        console.log('error?.response?.data?.error', error?.response);
        reject(error?.response || error);
      });
  });
};
